import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { inputCls } from '../../lib/inputCls';
import { cn } from '@siliconx/shared/ui/utils';
import { ManufacturerLogo } from './ManufacturerLogo';
import {
  formatUsd,
  formatRangeText,
  extractAssetClassIds,
  isManufacturerGateOpen,
  isModelGateOpen,
  filterLinesByAssetClass,
  filterByNeedle,
  stripMake,
} from '@siliconx/shared/fmv/browse';

/**
 * Shared Make + Model catalog picker — the A6 layout settled in
 * `apps/customer/src/components/shared/prototype/FamilyPickerVariantsA.tsx`
 * (SIL-953; the prototype itself is not shipped). Used by the add-asset
 * screen (apps/customer/src/routes/assets/submit.tsx); extracted so any
 * other form needing the same catalog lookup gets the exact same picker.
 *
 * Row 1: Manufacturer (custom listbox — a native <select> option can't carry
 * a brand logo) + Product line (a real <select>, options derived from the
 * matching root rows only). Between the rows: asset-class filter chips,
 * shown only when the chosen manufacturer spans more than one class. Row 2:
 * Model, a type-to-filter text field.
 *
 * Two gates:
 *   Gate 1 — nothing lists until a manufacturer is chosen (Product line and
 *   Model are disabled/empty-stated).
 *   Gate 2 — no models list until a product line is picked OR a specific
 *   asset class is chosen. The Model field stays enabled behind this gate:
 *   typing narrows the product-line options, and narrowing to a single line
 *   auto-selects it (opening the gate).
 *
 * Choosing a manufacturer is the only way out of a filter, so it is NEVER
 * itself filtered, and picking one resets the asset class, the product
 * line, the model text, and the current pick.
 *
 * "Product line" = a `sku` row with `grain='master'` (its variants point at
 * it via `parent_sku_id`) OR a standalone `grain='variant'` root row with no
 * children (SIL-774) — both are root rows from GET /api/client/model-suggest
 * `?make=`. Picking a master fetches its own variants (`?parentSkuId=`) for
 * the Model list; picking a standalone root makes it its own (only) model
 * row. A master can also be submitted directly — "Unknown — price as family
 * range" (SIL-768) — without picking one of its variants.
 *
 * Free-text (off-catalog) entry stays allowed: a caller that requires a
 * catalog match enforces it via the emitted skuId, same as before A6.
 */

export interface ModelSuggestItem {
  skuId: string;
  modelName: string;
  assetClassId: string | null;
  assetClassName: string | null;
  hasPricing: boolean;
  grain: 'master' | 'variant';
  parentSkuId: string | null;
  /** SIL-953: point price / family range + comp sample size, already
   * resolved onto v_sku_catalog (migration 789) — no extra join. */
  baselinePriceUsd: number | null;
  rangeLowUsd: number | null;
  rangeHighUsd: number | null;
  sampleSize: number | null;
  /** Live child count — masters only; null for variants. */
  variantCount: number | null;
}

// formatUsd/formatRangeText (currency formatting + degenerate-range
// collapse) now live in packages/shared/src/fmv/browse.ts — see that
// module's header for why this picker and the public widget must share one
// definition. `usd`/`rangeText` aliases kept locally so the JSX below reads
// the same as before.
const usd = formatUsd;
const rangeText = formatRangeText;

/** The price to show for a product-line option OR a model row.
 *
 * `baselinePriceUsd` is NULL for every master by construction (migration
 * 789: masters get `price_basis = 'family_range'`, never a point price) —
 * it is not a "no data" signal for a master the way it is for a variant.
 * A zero-configuration master (no live children) is still a valid pick
 * with a real, priced family range, and both the product-line <select> and
 * a model row can render the SAME master — they must agree. Reading
 * `baselinePriceUsd` without this `grain` check is exactly what produced a
 * master showing its range in the line dropdown ("$19,999") and "no comps"
 * for the identical SKU one row down in the model list (prod defect,
 * 2026-09-05: NVIDIA QUANTUM 2 QM9700). "no comps" must mean no comps —
 * never "this is a master". */
function rowPriceLabel(r: { grain: 'master' | 'variant'; baselinePriceUsd: number | null; rangeLowUsd: number | null; rangeHighUsd: number | null }): string {
  if (r.grain === 'master') {
    const range = rangeText(r.rangeLowUsd, r.rangeHighUsd);
    return range !== '—' ? range : 'no comps';
  }
  return usd(r.baselinePriceUsd) ?? 'no comps';
}

export function MakeModelPicker({
  make,
  model,
  onMakeChange,
  onModelChange,
  onPick,
  onClear,
  onAssetClassChange,
  className,
}: {
  make: string;
  model: string;
  onMakeChange: (make: string) => void;
  /** Free-text edit of the model field (no catalog match). */
  onModelChange: (model: string) => void;
  /** A catalog model (or a master's own "unknown configuration" row) was picked. */
  onPick: (m: ModelSuggestItem) => void;
  /** Model field cleared via the ✕ button. */
  onClear: () => void;
  /**
   * The derived, read-only asset class for the current product-line
   * selection — null once cleared. Fires from an effect on every selection
   * change, so the caller MUST memoise this (useCallback) and guard
   * same-value writes; an unguarded setState here re-renders forever.
   */
  onAssetClassChange?: (assetClass: string | null) => void;
  className?: string;
}) {
  const { data: ref } = useQuery({
    queryKey: qk.reference(),
    queryFn: async () => {
      const res = await api.api.client.reference.$get();
      await throwIfNotOk(res);
      return res.json();
    },
  });
  // A manufacturer with zero product lines is a dead end the moment it's
  // picked (Gate 1 would open onto nothing) — suppress it from the listbox
  // entirely rather than let the user discover that by picking it.
  const manufacturers = useMemo(
    () => (ref?.manufacturers ?? []).filter((m) => m.productLineCount > 0),
    [ref],
  );
  const selectedMake = manufacturers.find((m) => m.name === make) ?? null;

  const [makeOpen, setMakeOpen] = useState(false);
  // Roving focus index for the manufacturer listbox (Up/Down/Enter) — kept
  // separate from the actual selection so arrowing around doesn't commit a
  // pick until Enter.
  const [activeIdx, setActiveIdx] = useState(0);
  // Keyed by assetClassId (matches @siliconx/shared/fmv/browse's gate/filter
  // predicates, and the public widget) — NOT assetClassName, so two classes
  // that happened to share a display name could never merge under one chip.
  // classNameById (below) resolves it back to display text for the chips.
  const [assetClassId, setAssetClassId] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string | null>(null);

  // Root rows (masters + standalone variants) for the chosen make — one
  // fetch per make, capped at 200 server-side; asset-class filtering happens
  // client-side (SIL-953: no server query param for it).
  const { data: suggest } = useQuery({
    queryKey: qk.modelSuggest(make),
    queryFn: async () => {
      const res = await api.api.client['model-suggest'].$get({ query: { make: make.trim() } });
      await throwIfNotOk(res);
      const { models } = await res.json();
      // skuId is vSkuCatalog's primary key (NOT NULL) — Kysely's codegen
      // widens it to nullable because the underlying view has no PK
      // metadata, not because the value is ever actually null.
      return { models: models.map((m) => ({ ...m, skuId: m.skuId as string })) as ModelSuggestItem[] };
    },
    enabled: !!make.trim(),
    staleTime: 5 * 60_000,
  });
  const roots = useMemo(() => suggest?.models ?? [], [suggest]);

  // Gate 1: a manufacturer is "chosen" exactly when `make` matches an entry
  // in `manufacturers` (the only way `make` gets set — see chooseMake below;
  // free text never reaches this field, only a listbox pick or a scan match
  // against a known manufacturer name — submit.tsx's applyScanToForm).
  const manufacturerGateOpen = isManufacturerGateOpen(selectedMake?.id ?? null);

  // assetClassId -> display name, for the chip labels (extractAssetClassIds
  // / filterLinesByAssetClass key on assetClassId; the id is what's actually
  // compared, the name is only ever shown).
  const classNameById = useMemo(
    () => new Map(roots.map((r) => [r.assetClassId, r.assetClassName])),
    [roots],
  );

  // Asset classes present among this make's product lines — chips only
  // render when there is more than one to choose between.
  const classes = useMemo(() => extractAssetClassIds(roots), [roots]);

  const classFiltered = useMemo(
    () => filterLinesByAssetClass(roots, assetClassId),
    [roots, assetClassId],
  );

  // While gate 2 is shut, typing in Model narrows PRODUCT LINE options by
  // their own name — the point of the interaction, not a model search yet.
  // Once a line is pinned (lineId set — by the <select> or by a model pick),
  // it stays in this list even if it no longer matches the needle: after a
  // pick, `model` holds the PICKED MODEL's name, not a line search string
  // (e.g. picking "H100 PCIE 80GB" out of the "NVIDIA H100" line), and
  // needle-filtering the line out of its own dropdown was exactly what
  // dropped the resolved asset class to null post-pick (SIL-953 defect 1).
  const lineOptions = useMemo(() => {
    const filtered = filterByNeedle(classFiltered, model);
    if (lineId && !filtered.some((r) => r.skuId === lineId)) {
      const pinned = classFiltered.find((r) => r.skuId === lineId);
      if (pinned) return [pinned, ...filtered];
    }
    return filtered;
  }, [classFiltered, model, lineId]);

  // A pinned line (lineId set) stays active regardless of subsequent typing —
  // only narrowing-to-a-single-match auto-selects BEFORE anything is pinned.
  const soleId = lineOptions.length === 1 ? lineOptions[0]!.skuId : null;
  const activeLineId = lineId ?? soleId;
  const activeLine = roots.find((r) => r.skuId === activeLineId) ?? null;

  // A picked master's own live variants — fetched only once one is active.
  const { data: variantSuggest } = useQuery({
    queryKey: qk.modelSuggestVariants(activeLine?.grain === 'master' ? activeLine.skuId : undefined),
    queryFn: async () => {
      const res = await api.api.client['model-suggest'].$get({ query: { parentSkuId: activeLine!.skuId } });
      await throwIfNotOk(res);
      const { models } = await res.json();
      return { models: models.map((m) => ({ ...m, skuId: m.skuId as string })) as ModelSuggestItem[] };
    },
    enabled: !!activeLine && activeLine.grain === 'master',
    staleTime: 5 * 60_000,
  });

  // Second gate: a manufacturer alone is too broad to list models under.
  const gateOpen = isModelGateOpen({ activeLineId, assetClassId });

  const models = useMemo(() => {
    if (activeLine?.grain === 'master') {
      return filterByNeedle(variantSuggest?.models ?? [], model);
    }
    if (activeLine) return [activeLine];
    // Asset-class-only gate — list every matching root; a master row here
    // still submits directly, priced as its family range.
    return filterByNeedle(classFiltered, model);
  }, [activeLine, variantSuggest, model, classFiltered]);

  // The catalog's class for the current product line — read-only, what the
  // form actually uses. Fires on every change; caller owns the same-value
  // guard (see the prop doc above).
  const resolvedClass = activeLine?.assetClassName ?? null;
  useEffect(() => {
    onAssetClassChange?.(resolvedClass);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller-owned identity/guard
  }, [resolvedClass]);

  function chooseMake(id: string) {
    const m = manufacturers.find((x) => x.id === id);
    if (!m) return;
    onMakeChange(m.name);
    setAssetClassId(null);
    setLineId(null);
    onModelChange('');
    onClear();
    setMakeOpen(false);
  }

  function chooseClass(c: string | null) {
    setAssetClassId((prev) => (prev === c ? null : c));
    setLineId(null);
  }

  function choosePick(m: ModelSuggestItem) {
    // Pin whichever product line this pick belongs to (SIL-953 defect 1) —
    // the model text is about to become the picked model's own name, which
    // would otherwise needle-filter the line right back out of lineOptions
    // and silently drop the resolved (read-only) asset class.
    setLineId(activeLine ? activeLine.skuId : m.skuId);
    onPick(m);
  }

  function handleClear() {
    onClear();
    setLineId(null);
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <span className="mb-1.5 block text-xs font-medium text-foreground" id="mmp-make-label">
            Manufacturer
          </span>
          <div
            className="relative"
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setMakeOpen(false); return; }
              if (!makeOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                e.preventDefault();
                setActiveIdx(Math.max(0, manufacturers.findIndex((m) => m.id === selectedMake?.id)));
                setMakeOpen(true);
                return;
              }
              if (!makeOpen) return;
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIdx((i) => Math.min(i + 1, manufacturers.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIdx((i) => Math.max(i - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const m = manufacturers[activeIdx];
                if (m) chooseMake(m.id);
              }
            }}
          >
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={makeOpen}
              aria-labelledby="mmp-make-label"
              onClick={() => {
                setActiveIdx(Math.max(0, manufacturers.findIndex((m) => m.id === selectedMake?.id)));
                setMakeOpen((o) => !o);
              }}
              className={cn(inputCls(false), 'flex items-center justify-between gap-2 text-left')}
            >
              <span className="min-w-0 truncate">
                {selectedMake ? (
                  <ManufacturerLogo id={selectedMake.id} name={selectedMake.name} size={16} />
                ) : (
                  <span className="text-muted-foreground">Select a manufacturer…</span>
                )}
              </span>
              <span aria-hidden className="shrink-0 text-muted-foreground">▾</span>
            </button>

            {makeOpen && (
              <>
                {/* click-away without a document listener */}
                <button
                  type="button"
                  aria-hidden
                  tabIndex={-1}
                  className="fixed inset-0 z-30 cursor-default"
                  onClick={() => setMakeOpen(false)}
                />
                <ul
                  role="listbox"
                  aria-labelledby="mmp-make-label"
                  className="absolute z-40 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg"
                >
                  {manufacturers.map((m, i) => (
                    <li key={m.id} role="option" aria-selected={selectedMake?.id === m.id}>
                      <button
                        type="button"
                        onClick={() => chooseMake(m.id)}
                        onMouseEnter={() => setActiveIdx(i)}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-muted',
                          selectedMake?.id === m.id && 'bg-brand-50',
                          i === activeIdx && 'bg-muted',
                        )}
                      >
                        <span className="min-w-0 truncate text-sm text-foreground">
                          <ManufacturerLogo id={m.id} name={m.name} size={16} slot />
                        </span>
                        <span className="text-xs text-muted-foreground">{m.productLineCount}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <Field label="Product line" htmlFor="mmp-line">
          <select
            id="mmp-line"
            className={inputCls(false)}
            value={activeLineId ?? ''}
            disabled={!manufacturerGateOpen}
            onChange={(e) => setLineId(e.target.value || null)}
          >
            <option value="">
              {manufacturerGateOpen ? `${lineOptions.length} product line${lineOptions.length === 1 ? '' : 's'}…` : 'Select a manufacturer first'}
            </option>
            {lineOptions.map((r) => {
              const isEmptyMaster = r.grain === 'master' && !r.variantCount;
              const countLabel =
                r.grain === 'master'
                  ? `${r.variantCount ?? 0} config${(r.variantCount ?? 0) === 1 ? '' : 's'}`
                  : '1 config';
              return (
                <option key={r.skuId} value={r.skuId}>
                  {r.modelName} — {countLabel}{isEmptyMaster ? ' (not yet configured)' : ''}, {rowPriceLabel(r)}
                </option>
              );
            })}
          </select>
        </Field>
      </div>

      {manufacturerGateOpen && classes.length > 1 && (
        <div>
          <span className="mb-1.5 block text-xs font-medium text-foreground">Asset class</span>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => chooseClass(null)}
              className={chipCls(assetClassId === null)}
            >
              All classes
            </button>
            {classes.map((c) => (
              <button key={c} type="button" onClick={() => chooseClass(c)} className={chipCls(assetClassId === c)}>
                {classNameById.get(c) ?? c}
              </button>
            ))}
          </div>
        </div>
      )}

      <Field label="Model" htmlFor="mmp-model">
        <div className="relative">
          <input
            id="mmp-model"
            className={cn(inputCls(false), model && 'pr-9')}
            value={model}
            disabled={!manufacturerGateOpen}
            onChange={(e) => onModelChange(e.target.value)}
            placeholder={manufacturerGateOpen ? 'Type to filter models…' : 'Select a manufacturer first'}
          />
          {model && (
            <button
              type="button"
              aria-label="Clear model"
              onMouseDown={(e) => {
                e.preventDefault();
                handleClear();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {manufacturerGateOpen && (
          <p className="mt-1 text-xs text-muted-foreground">
            {gateOpen
              ? `${models.length} model${models.length === 1 ? '' : 's'}`
              : `${lineOptions.length} product line${lineOptions.length === 1 ? '' : 's'}`}
          </p>
        )}
      </Field>

      {!manufacturerGateOpen ? (
        <EmptyState>Choose a manufacturer to see its catalog.</EmptyState>
      ) : !gateOpen ? (
        <EmptyState>Select a product line or an asset class to list models.</EmptyState>
      ) : (
        // SIL-953 defect 3: a master with zero live children (111 in
        // production, mid-backfill elsewhere) is still a valid pick — it
        // submits as "Unknown — price as family range" — so this panel must
        // never render as a bare dead end just because `models` is empty.
        (models.length > 0 || activeLine?.grain === 'master') && (
          <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-card">
            {activeLine?.grain === 'master' && (
              <button
                type="button"
                onClick={() => choosePick(activeLine)}
                className="flex w-full items-baseline justify-between px-3 py-1.5 text-left hover:bg-muted"
              >
                <span className="text-sm text-foreground">Unknown — price as family range</span>
                <span className="shrink-0 pl-2 text-xs text-muted-foreground">
                  {rowPriceLabel(activeLine)}
                </span>
              </button>
            )}
            {models.map((m) => (
              <button
                key={m.skuId}
                type="button"
                onClick={() => choosePick(m)}
                className="flex w-full items-baseline justify-between px-3 py-1.5 text-left hover:bg-muted"
              >
                <span className="min-w-0 truncate text-sm text-foreground">
                  {stripMake(m.modelName, make)}
                  {!activeLine && m.assetClassName && (
                    <span className="ml-2 text-xs text-muted-foreground">{m.assetClassName}</span>
                  )}
                </span>
                <span className="shrink-0 pl-2 text-xs text-muted-foreground">{rowPriceLabel(m)}</span>
              </button>
            ))}
            {models.length === 0 && activeLine?.grain === 'master' && (
              <p className="px-3 py-3 text-xs text-muted-foreground">
                No specific configurations catalogued for this family yet — submit as "Unknown" above, or
                check back once the catalog is backfilled.
              </p>
            )}
          </div>
        )
      )}
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-border bg-card px-3 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

function chipCls(active: boolean) {
  return cn(
    'rounded-full border px-3 py-1.5 text-xs font-medium',
    active
      ? 'border-brand-500 bg-brand-50 text-brand-700'
      : 'border-border bg-card text-foreground hover:bg-muted',
  );
}

// stripMake now lives in packages/shared/src/fmv/browse.ts (imported above)
// — re-exported so submit.tsx's existing `import { stripMake } from
// '../../components/shared/MakeModelPicker'` keeps working unchanged.
export { stripMake };

function Field({
  label, htmlFor, children,
}: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-foreground" htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}
